// Ordering-legality planner for constraint absorption.
//
// A source op's constraint pipeline — `filter`, `sort`, `columns`,
// `limit` — is partly pushed to the provider's backend ("absorbed") and
// partly run client-side ("residual"). Absorbing the WRONG op silently
// changes the result: pushing a `limit` ahead of a residual `filter`
// would let the backend cap rows BEFORE the client-side filter runs, so
// the final result is short. This module owns the correctness rule that
// decides which constraints are legally absorbable.
//
// The rule (the commutativity table made executable):
//
//   > A provider may absorb constraint k only if every not-also-absorbed
//   > constraint before it commutes with it result-preservingly.
//   > Equivalently: the absorbed set is a LEGAL CONTIGUOUS PREFIX of the
//   > canonicalized constraint chain.
//
// Per-pair commutativity:
//   - filter ∘ filter         — commute (both restrict rows).
//   - columns (projection)    — commutes with filter/sort UNLESS it drops
//                               a column those ops reference.
//   - limit                   — does NOT commute with filter or sort;
//                               absorbable only if every order-sensitive
//                               op before it was absorbed with ZERO
//                               residual (no latent under-count).
//   - sort                    — order-sensitive; absorbable only at the
//                               front of the order-sensitive suffix.
//
// Anything not legally absorbable falls to residual. Because absorption
// is result-preserving by construction, the agent never reasons about it.
//
// This planner is provider-agnostic: it consumes a description of WHICH
// constraints are present, which the provider CAN apply natively, and the
// filter partition outcome, then returns which constraints are absorbed
// vs residual. The provider translates only the absorbed set; the
// pipeline builder turns the residual set into replay `PlanNode`s.

/** The four constraint kinds a source pipeline can carry, in canonical order. */
export type ConstraintKind = 'filter' | 'sort' | 'columns' | 'limit';

/**
 * The canonical order constraints are considered in. Legality is defined
 * over THIS order — `filter` restricts rows first, `sort` then orders the
 * surviving rows, `columns` projects, and `limit` caps last. A provider
 * absorbs a legal contiguous prefix of this chain.
 */
export const CONSTRAINT_ORDER: readonly ConstraintKind[] = ['filter', 'sort', 'columns', 'limit'];

/**
 * One constraint's presence + provider capability. `present` is true when
 * the pipeline carries this constraint at all; `providerCapable` is true
 * when the provider's backend can apply it natively. A constraint that is
 * present but not provider-capable always falls to residual; a constraint
 * the provider could apply but which the legality rule forbids absorbing
 * also falls to residual.
 */
export type ConstraintCapability = {
  readonly present: boolean;
  readonly providerCapable: boolean;
};

/**
 * What the pipeline carries + what the provider can absorb, plus the one
 * fact the ordering rule turns on.
 *
 * `filterFullyPushed` is the pivotal input: true iff the `filter`
 * partition produced NO residual predicate (the whole predicate pushed).
 * A residual filter drops rows client-side AFTER the backend returns, so
 * any order-sensitive constraint (`sort` / `limit`) absorbed ahead of it
 * would observe the wrong row set — hence it taints the order-sensitive
 * prefix.
 */
export type AbsorbCapabilities = {
  /** The `filter` constraint (its pushable part always rides `native`). */
  readonly filter: ConstraintCapability;
  /** The filter partitioned with ZERO residual (the whole predicate pushed). */
  readonly filterFullyPushed: boolean;
  readonly sort: ConstraintCapability;
  /**
   * The `columns` projection. `providerCapable` MUST already fold in the
   * "does not drop a column a residual op references" check — that is a
   * caller concern (only the caller knows each residual op's column refs).
   */
  readonly columns: ConstraintCapability;
  readonly limit: ConstraintCapability;
};

/**
 * The legality decision: which constraints the provider absorbs and which
 * stay residual. Every constraint that is `present` lands in exactly one
 * of the two lists; an absent constraint appears in neither.
 */
export type AbsorbPlan = {
  /** Constraints the provider absorbs natively, in canonical order. */
  readonly absorbed: readonly ConstraintKind[];
  /** Constraints that must run client-side as residual ops, in canonical order. */
  readonly residual: readonly ConstraintKind[];
};

/**
 * Decide which constraints are legally absorbable given the provider's
 * capabilities and the filter partition outcome.
 *
 * Walks the canonical chain front-to-back, tracking whether the
 * order-sensitive prefix so far has been absorbed with ZERO residual. As
 * soon as an order-sensitive op stays residual (an unabsorbed filter
 * residual, or an unabsorbed sort), the clean prefix is broken and no
 * later order-sensitive op (`sort` / `limit`) may be absorbed.
 *
 *  - `filter`: when present, its pushable part is always absorbed (rides
 *    `native`); a partial push leaves a residual predicate, which both
 *    re-emits a residual `filter` op AND taints the prefix for sort/limit.
 *  - `sort`: order-sensitive; absorbs only when present, provider-capable,
 *    AND the prefix before it is clean ("front of the order-sensitive
 *    suffix"). Otherwise residual, and it taints the prefix for `limit`.
 *  - `columns`: order-insensitive projection; absorbs whenever present +
 *    provider-capable (non-dropping pre-folded by the caller). Neither
 *    needs nor breaks the clean prefix.
 *  - `limit`: caps rows last and does not commute with filter/sort;
 *    absorbs only when present, provider-capable, AND the whole
 *    order-sensitive prefix was absorbed with zero residual.
 */
export const planAbsorption = (caps: AbsorbCapabilities): AbsorbPlan => {
  const absorbed: ConstraintKind[] = [];
  const residual: ConstraintKind[] = [];

  // True while every order-sensitive op seen so far was absorbed with zero
  // residual. Once false it stays false — a downstream order-sensitive op
  // can never be legally absorbed past a residual order-sensitive op.
  let cleanOrderSensitivePrefix = true;

  if (caps.filter.present) {
    // The pushable part of a present filter always rides `native`, so the
    // filter counts as absorbed; but a partial push leaves a residual
    // predicate that runs client-side and taints the prefix.
    absorbed.push('filter');
    if (!caps.filterFullyPushed) {
      residual.push('filter');
      cleanOrderSensitivePrefix = false;
    }
  }

  if (caps.sort.present) {
    if (caps.sort.providerCapable && cleanOrderSensitivePrefix) {
      absorbed.push('sort');
    } else {
      residual.push('sort');
      cleanOrderSensitivePrefix = false;
    }
  }

  if (caps.columns.present) {
    if (caps.columns.providerCapable) {
      absorbed.push('columns');
    } else {
      residual.push('columns');
    }
  }

  if (caps.limit.present) {
    if (caps.limit.providerCapable && cleanOrderSensitivePrefix) {
      absorbed.push('limit');
    } else {
      residual.push('limit');
    }
  }

  return {
    absorbed: orderConstraints(absorbed),
    residual: orderConstraints(residual),
  };
};

/** Re-order a constraint list into the canonical chain order. */
const orderConstraints = (kinds: readonly ConstraintKind[]): readonly ConstraintKind[] =>
  CONSTRAINT_ORDER.filter((k) => kinds.includes(k));
